/**
 * Utilities for building field mapping change details from v3 segment-scoped patches.
 */

export interface FieldMappingPatchesV3 {
  [segmentId: string]: {
    [fieldKey: string]: {
      sources?: string | string[] | null
      defaultValue?: unknown | null
      changedAt: string
      changedBy?: string
      changedByUserId?: string
    }
  }
}

export interface FieldMappingChangeDetailsV3 {
  hasChanges: boolean
  totalModified: number
  segments: Record<
    string,
    {
      fieldsCount: number
      modifiedFields: Array<{
        fieldKey: string
        before: {
          sources: string | string[]
          defaultValue?: unknown
        }
        after: {
          sources: string | string[]
          defaultValue?: unknown
        }
      }>
    }
  >
}

/**
 * Build field mapping change details from v3 segment-scoped patches.
 * Requires published config to show before/after comparison.
 */
export function buildFieldMappingChangesFromV3Patches(params: {
  publishedMappingConfig: unknown
  patches: FieldMappingPatchesV3
}): FieldMappingChangeDetailsV3 {
  const { publishedMappingConfig, patches } = params

  const segments: FieldMappingChangeDetailsV3['segments'] = {}
  let totalModified = 0

  // Build a map of published field values by segment
  const publishedBySegment = new Map<string, Map<string, any>>()
  const pubConfig = (publishedMappingConfig as any)?.mapping_config || {}

  for (const [segmentId, segmentData] of Object.entries(pubConfig)) {
    const mappings = (segmentData as any)?.field_mapping?.mappings || []
    const fieldMap = new Map<string, any>()
    for (const mapping of mappings) {
      if (mapping?.field) {
        fieldMap.set(mapping.field, {
          sources: mapping.sources,
          defaultValue: mapping.default_value ?? mapping.policy?.default_value,
        })
      }
    }
    publishedBySegment.set(segmentId, fieldMap)
  }

  for (const [segmentId, segmentPatches] of Object.entries(patches)) {
    const publishedFields = publishedBySegment.get(segmentId) || new Map()
    const modifiedFields: FieldMappingChangeDetailsV3['segments'][string]['modifiedFields'] = []

    for (const [fieldKey, patch] of Object.entries(segmentPatches)) {
      const before = publishedFields.get(fieldKey) || { sources: [], defaultValue: undefined }
      const after = {
        sources: patch.sources ?? [],
        defaultValue: patch.defaultValue,
      }

      modifiedFields.push({
        fieldKey,
        before,
        after,
      })
      totalModified++
    }

    if (modifiedFields.length > 0) {
      segments[segmentId] = {
        fieldsCount: modifiedFields.length,
        modifiedFields,
      }
    }
  }

  return {
    hasChanges: totalModified > 0,
    totalModified,
    segments,
  }
}
